/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type CompletePayload,
  type Achievements,
  type CreateHomeTaskPayload,
  type HomeTask,
  homeApi,
} from '../api/home.js';

const tasksKey = (clientId: string): readonly unknown[] => ['home-tasks', clientId];
const achievementsKey = (clientId: string): readonly unknown[] => [
  'home-tasks',
  'achievements',
  clientId,
];

export function useHomeTasks(clientId: string | undefined): UseQueryResult<readonly HomeTask[]> {
  return useQuery({
    queryKey: tasksKey(clientId ?? ''),
    queryFn: () => homeApi.list(clientId ?? ''),
    enabled: clientId !== undefined && clientId !== '',
  });
}

export function useAchievements(clientId: string | undefined): UseQueryResult<Achievements> {
  return useQuery({
    queryKey: achievementsKey(clientId ?? ''),
    queryFn: () => homeApi.achievements(clientId ?? ''),
    enabled: clientId !== undefined && clientId !== '',
  });
}

export function useCreateHomeTask(
  clientId: string,
): UseMutationResult<HomeTask, Error, CreateHomeTaskPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateHomeTaskPayload) => homeApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tasksKey(clientId) });
    },
  });
}

interface CompleteVars {
  readonly id: string;
  readonly payload: CompletePayload;
}

export function useCompleteHomeTask(
  clientId: string,
): UseMutationResult<void, Error, CompleteVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: CompleteVars) => homeApi.complete(vars.id, vars.payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tasksKey(clientId) });
    },
  });
}
